/**
 * Activity — customer-facing log of booking events.
 *
 * Renders the unified feed returned by the `customer-activity-feed` Edge
 * Function (see supabase/functions/customer-activity-feed/index.ts). One
 * row per event; mobile-first single-column layout matching the Profile /
 * Dashboard glass aesthetic.
 *
 * URL params:
 *   ?trip_id=<uuid>  — when present, the feed is restricted to a single
 *                       booking and a "Viewing: <trip ref>" chip appears at
 *                       the top with a Clear button.
 *   ?kinds=<csv>     — preselect category filter chips.
 *
 * Why no email-equivalent: this page is the substitute for the transactional
 * emails the NMI track doesn't send. Every event the customer would want a
 * receipt for is here.
 */

import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Activity as ActivityIcon,
  CreditCard,
  Plane,
  CalendarClock,
  Coins,
  ArrowRight,
  ChevronDown,
  RefreshCw,
  Filter,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  X,
  ShieldAlert,
  User as UserIcon,
  PlaneTakeoff,
  Wallet,
  TrendingUp,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { User as UserEntity } from "@/api/entities";
import { customerActivityFeedUrl, customerUpcomingNmiPaymentsUrl } from "@/lib/apiUrls";
import { mergeSupabaseEdgeHeaders } from "@/lib/reserveActAs";
import { cn } from "@/lib/utils";
import RoyalAmbientBackground from "@/components/ui/RoyalAmbientBackground";
import { fmtCurrency } from '@/utils/money';
import VipBadge from '@/components/admin/VipBadge';
import {
  CART_SECTION_CARD,
  DASHBOARD_TAB_ACTIVE,
  DASHBOARD_TAB_INACTIVE,
} from "@/constants/brandColors";

// Royal-blue accent used across the Dashboard masthead/metric ribbon —
// keep Activity in lockstep so the two pages feel like one product surface.
// Use the literal `text-[hsl(228,35%,58%)]/N` class strings inline so the
// Tailwind JIT can statically extract them.

// Lightweight row shell for the dense inline lists (Activity feed +
// Upcoming NMI). The full CART_SECTION_CARD gradient is too heavy when
// stacked 20+ deep — it turns the page into a wall of blue panels. This
// strips the gradient to a hairline border + a near-transparent royal
// wash that only fills in on hover, so the data is what carries the eye.
const ROW_SHELL =
  "border border-brand-800/35 bg-brand-950/30 hover:bg-brand-900/30 transition-colors rounded-lg";

// ── Category metadata ────────────────────────────────────────────────────────
// Single source of truth for the icon + accent color used per category.
// Categories come from the Edge function payload (`event.category`) so adding
// a new category here is the only frontend change needed when we widen the
// aggregator later.
const CATEGORY_META = {
  payment: { label: "Payments", Icon: CreditCard,    accent: "text-emerald-300", bg: "bg-emerald-500/10" },
  flight:  { label: "Flight",   Icon: Plane,         accent: "text-sky-300",     bg: "bg-sky-500/10" },
  schedule:{ label: "Schedule", Icon: CalendarClock, accent: "text-amber-300",   bg: "bg-amber-500/10" },
  card:    { label: "Cards",    Icon: CreditCard,    accent: "text-violet-300",  bg: "bg-violet-500/10" },
  credit:  { label: "Credits",  Icon: Coins,         accent: "text-yellow-300",  bg: "bg-yellow-500/10" },
  booking: { label: "Bookings", Icon: PlaneTakeoff,  accent: "text-indigo-300",  bg: "bg-indigo-500/10" },
};

// Map specific kinds to a status pill style. Anything not listed uses the
// generic "neutral" style.
function statusBadge(kind, status) {
  if (!status && !kind) return null;
  // Success-flavored
  if (kind === "installment_paid" || kind === "installment_credited"
    || (kind.startsWith("manual_payment_") && status === "applied")) {
    return { Icon: CheckCircle2, label: status === "applied" ? "Paid" : "Paid", tone: "text-emerald-300 bg-emerald-500/10" };
  }
  // Retry-in-flight
  if (kind === "installment_retrying"
    || kind.startsWith("manual_payment_pending_")) {
    return { Icon: RefreshCw, label: "Retrying", tone: "text-amber-300 bg-amber-500/10" };
  }
  // Hard failure
  if (kind === "installment_failed" || kind === "installment_delinquent"
    || kind.startsWith("manual_payment_failed_")) {
    return { Icon: XCircle, label: "Failed", tone: "text-red-300 bg-red-500/10" };
  }
  // Cancelled
  if (kind === "trip_cancelled" || kind === "installment_cancelled") {
    return { Icon: XCircle, label: "Cancelled", tone: "text-white/70 bg-white/10" };
  }
  if (kind === "schedule_changed") {
    return { Icon: CalendarClock, label: "Schedule", tone: "text-amber-300 bg-amber-500/10" };
  }
  if (kind === "flight_changed") {
    return { Icon: Plane, label: "Updated", tone: "text-sky-300 bg-sky-500/10" };
  }
  if (kind === "card_updated") {
    return { Icon: CreditCard, label: "Card updated", tone: "text-violet-300 bg-violet-500/10" };
  }
  if (kind.startsWith("credit_") || kind.startsWith("miles_")) {
    return { Icon: Sparkles, label: kind.startsWith("miles_") ? "Miles" : "Credit", tone: "text-yellow-300 bg-yellow-500/10" };
  }
  return null;
}

// ── Formatters ───────────────────────────────────────────────────────────────

function formatDollars(cents) {
  if (cents == null || !Number.isFinite(Number(cents))) return null;
  const n = Number(cents);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${fmtCurrency(Math.abs(n))}`;
}

function formatDateLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWeek = Math.round(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek}w ago`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  const diffYear = Math.round(diffDay / 365);
  return `${diffYear}y ago`;
}

// ── Components ───────────────────────────────────────────────────────────────

// Small pill that jumps to the booking's Dashboard flight modal.
//
// Uses the established `sessionStorage.open_booking_id` handshake (same
// pattern as Accounting.jsx) rather than a `?show_flight_id=` URL query.
// Why: Dashboard calls `window.history.replaceState(...)` on mount to
// scrub query strings from the address bar, which races with the async
// flightsData fetch and can wipe the URL before the modal actually
// opens — leaving the admin staring at a bare /trips page. sessionStorage
// has no such race; Dashboard consumes the key once flightsData is loaded.
// Preferred identifier is the booking UUID (exact match in DB), falling
// back to trip_reference when the activity event has no booking_id.
function ViewFlightButton({ tripReference, bookingId, className = "" }) {
  const navigate = useNavigate();
  const id = bookingId || tripReference;
  if (!id) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      sessionStorage.setItem("open_booking_id", id);
    } catch {
      // Storage may be disabled (private mode, etc.). Fall back to the
      // query-string path so the link still does *something* useful.
    }
    navigate(`/trips?show_flight_id=${encodeURIComponent(id)}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title="Open this booking in the Dashboard"
      aria-label="Open this booking in the Dashboard"
      className={cn(
        "inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[9px] uppercase tracking-wider font-medium",
        "border border-brand-700/50 bg-brand-900/40 text-white/70",
        "hover:text-white hover:bg-brand-800/60 hover:border-brand-600/60 transition-colors",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-600/40",
        className,
      )}
    >
      <ExternalLink className="w-2.5 h-2.5" />
      View flight
    </button>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "h-7 px-3 rounded-full text-[11px] font-medium tracking-wide whitespace-nowrap transition-all border",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-600/40",
        active ? DASHBOARD_TAB_ACTIVE : DASHBOARD_TAB_INACTIVE,
      )}
    >
      {children}
    </button>
  );
}

function EventCard({ event, showCustomer }) {
  const [open, setOpen] = useState(false);
  const meta = CATEGORY_META[event.category] || CATEGORY_META.payment;
  const Icon = meta.Icon;
  const badge = statusBadge(event.kind || "", event.status || "");
  const amount = formatDollars(event.amount_cents);

  const hasDetails = event.details && typeof event.details === "object"
    && Object.keys(event.details).some((k) => event.details[k] != null && event.details[k] !== "");

  const tripLabel = event.trip_reference
    ? `Trip ${event.trip_reference}`
    : event.booking_id
      ? `Booking ${event.booking_id.slice(0, 8)}…`
      : null;

  // Two-row dense layout — the right gutter is finally pulling its weight.
  //   Row 1 (header):  [icon]  summary text ───────  $amount  [badge]  ⌄
  //   Row 2 (meta):            email · time · trip
  // The whole card is the toggle when details exist, so we don't waste a
  // third row on a "Details" button. Reads like a bank transaction row.
  const cardBody = (
    <div className="flex items-center gap-2.5">
      <div className="shrink-0 w-7 h-7 rounded-full bg-brand-900/40 border border-brand-800/40 flex items-center justify-center">
        <Icon className={`w-3.5 h-3.5 ${meta.accent}`} />
      </div>

      <div className="flex-1 min-w-0">
        {/* Row 1: summary + amount/badge/chevron pinned to the right */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-white font-normal leading-snug truncate min-w-0">
            {event.summary}
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            {amount != null ? (
              <span className="text-sm text-white font-normal tabular-nums leading-none">{amount}</span>
            ) : null}
            {badge ? (
              <span className={`inline-flex items-center gap-1 px-1.5 rounded-full text-[9px] uppercase tracking-wider h-4 ${badge.tone}`}>
                <badge.Icon className="w-2.5 h-2.5" />
                {badge.label}
              </span>
            ) : null}
            <ViewFlightButton
              tripReference={event.trip_reference}
              bookingId={event.booking_id}
            />
            {hasDetails ? (
              <ChevronDown
                className={`w-3 h-3 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            ) : null}
          </div>
        </div>

        {/* Row 2: single dense meta line (customer · time · trip).
            On mobile we anchor `time · trip` to the right and let the
            customer label absorb whatever slack is left — that keeps the
            trip ref always visible (it's the primary scan target) even on
            a 320px screen. We prefer the user's display name (from
            auth.user_metadata) and fall back to email when the user
            hasn't set one. */}
        {(() => {
          const customerLabel = event.user_name || event.user_email;
          const showCustomerRow = showCustomer && customerLabel;
          return (
            <div className="mt-0.5 flex items-center gap-x-1.5 text-[10px] text-white/45 font-normal leading-tight min-w-0">
              {showCustomerRow ? (
                <span
                  className="inline-flex items-center gap-0.5 min-w-0 flex-1 max-w-[16ch] sm:max-w-[30ch]"
                  title={event.user_email || undefined}
                >
                  <UserIcon className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate">{customerLabel}</span>
                </span>
              ) : null}
              {showCustomerRow ? (
                <span className="text-white/25 shrink-0">·</span>
              ) : null}
              <span className="shrink-0" title={formatDateLong(event.occurred_at)}>
                {formatRelative(event.occurred_at)}
              </span>
              {tripLabel ? (
                <>
                  <span className="text-white/25 shrink-0">·</span>
                  <span className="truncate shrink-0">{tripLabel}</span>
                </>
              ) : null}
            </div>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div className={ROW_SHELL}>
      {hasDetails ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full text-left p-2 md:p-2.5 rounded-lg focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-600/40"
          aria-expanded={open}
        >
          {cardBody}
        </button>
      ) : (
        <div className="p-2 md:p-2.5">{cardBody}</div>
      )}

      <AnimatePresence initial={false}>
        {open && hasDetails ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            {/* Indent under the content column on tablet+; flush on mobile
                so the details get the full row width. */}
            <div className="px-2 sm:pl-12 sm:pr-2.5 pb-2 sm:pb-2.5">
              <DetailsBlock event={event} />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function DetailsBlock({ event }) {
  const rows = useMemo(() => {
    const out = [];
    out.push(["When", formatDateLong(event.occurred_at)]);
    const d = event.details || {};
    // Domain-aware formatting
    if (d.installment_number != null) out.push(["Installment", `#${d.installment_number}`]);
    if (d.retry_count) out.push(["Retry count", String(d.retry_count)]);
    if (d.gateway_response_text) out.push(["Gateway response", String(d.gateway_response_text)]);
    if (d.nmi_transaction_id) out.push(["NMI transaction", String(d.nmi_transaction_id)]);
    if (d.processor) out.push(["Processor", String(d.processor).toUpperCase()]);
    if (d.purpose) out.push(["Purpose", String(d.purpose).replace(/^defera_capital_/, "").replace(/_/g, " ")]);
    if (d.previous_grand_total_cents != null) out.push(["Previous total", formatDollars(d.previous_grand_total_cents)]);
    if (d.new_grand_total_cents != null) out.push(["New total", formatDollars(d.new_grand_total_cents)]);
    if (d.price_diff_cents != null && d.price_diff_cents !== 0) out.push(["Price difference", formatDollars(d.price_diff_cents)]);
    if (d.flight_credit_cents) out.push(["Flight credit", formatDollars(d.flight_credit_cents)]);
    if (d.last4) out.push(["Card", `${d.brand ? `${d.brand.toUpperCase()} ` : ""}•••• ${d.last4}`]);
    if (d.exp_month && d.exp_year) out.push(["Expires", `${String(d.exp_month).padStart(2, "0")}/${String(d.exp_year).slice(-2)}`]);
    if (d.description) out.push(["Note", String(d.description)]);
    if (d.reason) out.push(["Reason", String(d.reason)]);
    if (d.transaction_type) out.push(["Type", String(d.transaction_type).replace(/_/g, " ")]);
    if (d.amount != null && !out.find(([k]) => k === "Amount")) out.push(["Amount", String(d.amount)]);
    if (event.booking_id && !event.trip_reference) {
      out.push(["Booking", event.booking_id]);
    }
    return out;
  }, [event]);

  return (
    <div className="rounded-lg p-2 md:p-2.5 bg-brand-900/30 border border-brand-800/40">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-normal">
        {rows.map(([k, v]) => (
          <div key={k} className="flex sm:block items-baseline justify-between gap-2">
            <dt className="text-white/45">{k}</dt>
            <dd className="text-white/80 break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function EmptyState({ filterActive }) {
  return (
    <div className={cn(CART_SECTION_CARD, "rounded-2xl")}>
      <div className="p-6 text-center">
        <div className="w-10 h-10 mx-auto mb-2 rounded-2xl bg-brand-900/40 border border-brand-800/40 flex items-center justify-center">
          <ActivityIcon className="w-5 h-5 text-white/45" />
        </div>
        <p className="text-sm text-white font-normal mb-0.5">
          {filterActive ? "No activity matches this filter" : "No activity yet"}
        </p>
        <p className="text-white/60 text-xs font-normal">
          {filterActive
            ? "Try clearing the filter to see everything."
            : "Payments, flight changes, and other booking activity will appear here."}
        </p>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className={cn(CART_SECTION_CARD, "rounded-2xl border-red-400/30")}>
      <div className="p-3.5 flex items-start gap-2.5">
        <AlertCircle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-white font-normal mb-0.5">Could not load activity</p>
          <p className="text-white/60 text-xs font-normal mb-2">{message}</p>
          <Button
            size="sm"
            variant="outline"
            className={cn(
              "h-7 text-xs font-normal text-white border",
              DASHBOARD_TAB_INACTIVE,
            )}
            onClick={onRetry}
          >
            <RefreshCw className="w-3 h-3 mr-1.5" />
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className={cn(CART_SECTION_CARD, "rounded-xl")}>
          <div className="p-2.5 md:p-3">
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 rounded-full bg-brand-800/40 animate-pulse" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 bg-brand-800/40 rounded animate-pulse w-3/4" />
                <div className="h-2.5 bg-brand-800/40 rounded animate-pulse w-1/3" />
              </div>
              <div className="w-14 h-3.5 bg-brand-800/40 rounded animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Date-grouping helpers ────────────────────────────────────────────────────

function dayBucketLabel(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

function groupEventsByDay(events) {
  const groups = [];
  let currentLabel = null;
  let currentBucket = null;
  for (const e of events) {
    const label = dayBucketLabel(e.occurred_at);
    if (label !== currentLabel) {
      currentLabel = label;
      currentBucket = { label, events: [] };
      groups.push(currentBucket);
    }
    currentBucket.events.push(e);
  }
  return groups;
}

// ── Upcoming NMI payments view ───────────────────────────────────────────────
// Renders an inline list of every booking with open NMI installments so we can
// verify the daily cron is actually firing without inspecting the table by hand.
// Source of truth: `customer-upcoming-nmi-payments` Edge Function — admin-only.

// NMI due dates are stored as UTC midnight of the plan's calendar day (see
// nmi-installment-engine.seedInstallmentRows), so they MUST be rendered in
// UTC. Rendering them in browser-local time showed every date one day early
// for admins west of UTC — "Today · Jul 2" for a payment the cron treats as
// Jul 3 — which read as "the cron missed today's charge".
function formatDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Days between the due date's UTC calendar day and the admin's local
 *  calendar day today. Anchoring "today" locally keeps "Tomorrow" aligned
 *  with when the charge cron will actually fire from the admin's viewpoint. */
function daysUntil(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const now = new Date();
  const dueDay = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate());
  const todayLocal = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dueDay - todayLocal) / 86400000);
}

function relativeDueLabel(iso) {
  const d = daysUntil(iso);
  if (d == null) return "—";
  if (d === 0) return "Today";
  if (d === 1) return "Tomorrow";
  if (d === -1) return "Yesterday";
  if (d > 0) return `in ${d} day${d === 1 ? "" : "s"}`;
  return `${Math.abs(d)} day${d === -1 ? "" : "s"} ago`;
}

function brandLabel(brand) {
  if (!brand) return "Card";
  const map = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover" };
  return map[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1);
}

const INSTALLMENT_STATUS_TONE = {
  pending:    { tone: "text-white/70 bg-brand-800/40",        label: "Pending" },
  retrying:   { tone: "text-amber-300 bg-amber-500/10",       label: "Retrying" },
  declined:   { tone: "text-red-300 bg-red-500/10",           label: "Declined" },
  error:      { tone: "text-red-300 bg-red-500/10",           label: "Error" },
  // Terminal rows — included for context so the plan doesn't appear to
  // start at #2 once #1 is paid.
  success:    { tone: "text-emerald-300 bg-emerald-500/10",   label: "Paid",       terminal: true },
  superseded: { tone: "text-emerald-300/80 bg-emerald-500/5", label: "Paid early", terminal: true },
  credited:   { tone: "text-sky-300 bg-sky-500/10",           label: "Credited",   terminal: true },
  skipped:    { tone: "text-white/50 bg-white/5",             label: "Skipped",    terminal: true },
  cancelled:  { tone: "text-white/40 bg-white/5",             label: "Cancelled",  terminal: true },
  delinquent: { tone: "text-red-300 bg-red-500/10",           label: "Delinquent", terminal: true },
};

function InstallmentRow({ inst }) {
  const meta = INSTALLMENT_STATUS_TONE[inst.status] || INSTALLMENT_STATUS_TONE.pending;
  const due = daysUntil(inst.due_date);
  // A settled row can't be "overdue" no matter what its due date says.
  const overdue = !meta.terminal && due != null && due < 0;
  return (
    <div className={`flex items-center gap-2 py-1 px-2 rounded-md bg-brand-900/30 border border-brand-800/40 ${meta.terminal ? "opacity-70" : ""}`}>
      <div className="shrink-0 w-6 text-center text-white/40 text-[10px] font-normal tabular-nums">
        #{inst.installment_number}
      </div>
      <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <span className="text-white text-xs font-normal tabular-nums leading-tight">
          {formatDollars(inst.amount_cents)}
        </span>
        <span className="text-white/45 text-[10px] font-normal leading-tight">
          {formatDateShort(inst.due_date)}
        </span>
        {meta.terminal ? (
          inst.attempted_at ? (
            <span className="text-white/45 text-[10px] font-normal leading-tight">
              {formatRelative(inst.attempted_at)}
            </span>
          ) : null
        ) : (
          <span className={`text-[10px] font-normal leading-tight ${overdue ? "text-red-300" : "text-white/45"}`}>
            {relativeDueLabel(inst.due_date)}
          </span>
        )}
        {inst.retry_count > 0 ? (
          <span className="text-white/45 text-[10px] font-normal leading-tight">retry #{inst.retry_count}</span>
        ) : null}
      </div>
      <span className={`inline-flex items-center px-1.5 rounded-full text-[9px] uppercase tracking-wider h-4 ${meta.tone}`}>
        {meta.label}
      </span>
    </div>
  );
}

function UpcomingBookingCard({ bucket }) {
  const [open, setOpen] = useState(false);
  const hasPastDue = bucket.past_due_count > 0;
  const nextDueDays = daysUntil(bucket.next_due_at);
  const nextDueClass = hasPastDue
    ? "text-red-300"
    : nextDueDays != null && nextDueDays <= 2
      ? "text-amber-300"
      : "text-white/80";

  // Compact card-on-file label, e.g. "Mastercard ••5278 · 12/30 · biweekly".
  // Concatenating into one line saves a vertical row in the metadata strip.
  const cardOnFile = (() => {
    const parts = [];
    if (bucket.card.brand || bucket.card.last4) {
      parts.push(`${brandLabel(bucket.card.brand)} ••${bucket.card.last4 || "????"}`);
    }
    if (bucket.card.exp_month && bucket.card.exp_year) {
      parts.push(`${String(bucket.card.exp_month).padStart(2, "0")}/${String(bucket.card.exp_year).slice(-2)}`);
    }
    if (bucket.payment_frequency) parts.push(bucket.payment_frequency);
    return parts.join(" · ") || "—";
  })();

  const tripLabel = bucket.trip_reference
    || bucket.booking_reference
    || `Booking ${bucket.booking_id.slice(0, 8)}…`;

  // Two-row dense layout for NMI bookings:
  //   Row 1: trip ref + status badges     ──     next $ · when · ⌄
  //   Row 2 (meta strip, wraps): email · Remaining · Past due · Card · Last
  //          attempt · n installments (right-anchored)
  // Email lives in the meta strip rather than row 1 so a 320px mobile
  // viewport doesn't push the right gutter onto its own line.
  const cardBody = (
    <>
      {/* Row 1: identity on the left, money on the right */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1 flex items-center gap-1.5 flex-wrap leading-tight">
          <Link
            to={`/activity?trip_id=${bucket.booking_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-white font-normal hover:underline truncate"
          >
            {tripLabel}
          </Link>
          {bucket.status && bucket.status !== "confirmed" ? (
            <span className="inline-flex items-center px-1.5 rounded-full text-[9px] uppercase tracking-wider h-4 text-amber-300 bg-amber-500/10 shrink-0">
              {bucket.status}
            </span>
          ) : null}
          {hasPastDue ? (
            <span className="inline-flex items-center gap-0.5 px-1.5 rounded-full text-[9px] uppercase tracking-wider h-4 text-red-300 bg-red-500/10 shrink-0">
              <AlertTriangle className="w-2.5 h-2.5" />
              Past due
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 leading-none">
          <span className={`text-sm font-normal tabular-nums ${nextDueClass}`}>
            {formatDollars(bucket.next_amount_cents)}
          </span>
          {/* Date is verbose on mobile — show only the relative label there. */}
          <span className={`hidden sm:inline text-[10px] font-normal ${nextDueClass}`}>
            {relativeDueLabel(bucket.next_due_at)} · {formatDateShort(bucket.next_due_at)}
          </span>
          <span className={`sm:hidden text-[10px] font-normal ${nextDueClass}`}>
            {relativeDueLabel(bucket.next_due_at)}
          </span>
          <ViewFlightButton
            tripReference={bucket.trip_reference}
            bookingId={bucket.booking_id}
          />
          <ChevronDown
            className={`w-3 h-3 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Row 2: wrap-friendly meta strip. Customer leads (admin's scan target —
          prefer display name, fall back to email), then dollar metrics,
          card-on-file, last attempt, installment count. */}
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-[11px] font-normal leading-tight min-w-0">
        {(bucket.user_name || bucket.user_email) ? (
          <span
            className="inline-flex items-center gap-0.5 min-w-0 max-w-[18ch] sm:max-w-[30ch] text-[10px] text-white/55"
            title={bucket.user_email || undefined}
          >
            <UserIcon className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{bucket.user_name || bucket.user_email}</span>
          </span>
        ) : null}
        {/* eslint-disable-next-line react/prop-types -- the bucket shape is documented in customer-upcoming-nmi-payments */}
        <VipBadge userId={bucket.user_id || null} />
        <span>
          <span className="text-white/45">Remaining </span>
          <span className="text-white tabular-nums">{formatDollars(bucket.remaining_cents)}</span>
          <span className="text-white/45"> · {bucket.remaining_count}</span>
        </span>
        <span>
          <span className="text-white/45">Past due </span>
          <span className={`tabular-nums ${hasPastDue ? "text-red-300" : "text-white/45"}`}>
            {formatDollars(bucket.past_due_cents)}
          </span>
          {bucket.past_due_count > 0 ? (
            <span className="text-white/45"> · {bucket.past_due_count}</span>
          ) : null}
        </span>
        <span className="text-white/65 truncate min-w-0">{cardOnFile}</span>
        {bucket.last_attempt ? (
          <span className="text-[10px] text-white/45 truncate min-w-0 hidden sm:inline">
            <span className="text-white/35">Last </span>
            #{bucket.last_attempt.installment_number} {bucket.last_attempt.status}
            {bucket.last_attempt.retry_count ? ` (r${bucket.last_attempt.retry_count})` : ""}
            <span className="text-white/35"> · {formatRelative(bucket.last_attempt.attempted_at)}</span>
          </span>
        ) : (
          <span className="text-[10px] text-white/35 hidden sm:inline">No cron attempts yet</span>
        )}
        <span className="text-white/35 text-[10px] ml-auto shrink-0">
          {/* installments[] now includes settled rows for context; count the
              full plan but call out how many are already settled. */}
          {bucket.installments.length} installment{bucket.installments.length === 1 ? "" : "s"}
          {bucket.installments.length > bucket.remaining_count
            ? ` · ${bucket.installments.length - bucket.remaining_count} settled`
            : ""}
        </span>
      </div>
    </>
  );

  return (
    <div className={cn(ROW_SHELL, hasPastDue && "border-red-400/30")}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-2 md:p-2.5 rounded-lg focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-600/40"
        aria-expanded={open}
      >
        {cardBody}
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-2 md:px-2.5 pb-2 md:pb-2.5 space-y-1">
              {bucket.installments.map((inst) => (
                <InstallmentRow key={inst.id} inst={inst} />
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function UpcomingTotalsStrip({ totals }) {
  if (!totals) return null;
  // Inline metric ribbon — mirrors the Dashboard masthead. Hairline
  // dividers and tabular numerals instead of boxed KPI tiles so the
  // header stays calm and the booking cards below carry the visual
  // weight. Semantic accent colors are preserved on the values that
  // actually need to "shout" (upcoming = teal-emerald, past-due = red).
  const cells = [
    { label: "Bookings",      value: String(totals.bookings),              Icon: PlaneTakeoff,  tone: "text-white/90" },
    { label: "Open payments", value: String(totals.installments),          Icon: CalendarClock, tone: "text-white/90" },
    { label: "Upcoming",      value: formatDollars(totals.upcoming_cents), Icon: TrendingUp,    tone: "text-emerald-300" },
    { label: "Past due",      value: formatDollars(totals.past_due_cents), Icon: AlertTriangle, tone: totals.past_due_cents > 0 ? "text-red-300" : "text-white/55" },
  ];
  return (
    <div className="mb-3 flex items-center gap-3 md:gap-4 text-xs flex-wrap">
      {cells.map((c, idx) => (
        <Fragment key={c.label}>
          {idx > 0 ? (
            <span className="hidden sm:inline-block w-px h-3 bg-brand-700/40" aria-hidden="true" />
          ) : null}
          <div className="flex items-center gap-1.5">
            <c.Icon className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
            <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">{c.label}</span>
            <span className={cn("tabular-nums font-normal", c.tone)}>{c.value}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function UpcomingEmpty() {
  return (
    <div className={cn(CART_SECTION_CARD, "rounded-2xl")}>
      <div className="p-6 text-center">
        <div className="w-10 h-10 mx-auto mb-2 rounded-2xl bg-brand-900/40 border border-brand-800/40 flex items-center justify-center">
          <Wallet className="w-5 h-5 text-white/45" />
        </div>
        <p className="text-sm text-white font-normal mb-0.5">No upcoming NMI payments</p>
        <p className="text-white/60 text-xs font-normal">
          Either there are no active NMI bookings with open installments, or every installment has settled.
        </p>
      </div>
    </div>
  );
}

function UpcomingNmiList() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [totals, setTotals] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const load = useCallback(async () => {
    if (bookings.length === 0) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const { supabase } = await import("@/lib/supabase");
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError("Sign in to view upcoming payments.");
        return;
      }
      const res = await fetch(customerUpcomingNmiPaymentsUrl(), {
        method: "GET",
        headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.error || `Request failed (${res.status})`);
        return;
      }
      setBookings(Array.isArray(body.bookings) ? body.bookings : []);
      setTotals(body.totals || null);
      setGeneratedAt(body.generated_at || null);
    } catch (e) {
      setError(e?.message || "Could not load upcoming payments");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <UpcomingTotalsStrip totals={totals} />
      <div className="flex items-center justify-between mb-2">
        <div className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-normal">
          {generatedAt ? `Snapshot · ${formatRelative(generatedAt)}` : ""}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 px-2.5 rounded-full text-[11px] font-medium tracking-wide border",
            DASHBOARD_TAB_INACTIVE,
          )}
          onClick={load}
          disabled={refreshing}
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      {bookings.length === 0 ? (
        <UpcomingEmpty />
      ) : (
        <div className="space-y-1">
          {bookings.map((b) => (
            <UpcomingBookingCard key={b.booking_id} bucket={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function ViewModeToggle({ value, onChange }) {
  const tabs = [
    { id: "activity", label: "Activity",     Icon: ActivityIcon },
    { id: "upcoming", label: "Upcoming NMI", Icon: Wallet },
  ];
  // Mirrors Dashboard's segmented status tabs: brand-gradient pills with
  // a thin border and the royal-blue accent on inactive hover. Sits on
  // its own (no outer pill container) so the empty space breathes.
  return (
    <div className="inline-flex items-center gap-1.5">
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={cn(
              "h-8 px-3 rounded-full text-[11px] font-medium tracking-wide whitespace-nowrap transition-all flex items-center gap-1.5 border",
              "focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-600/40",
              active ? DASHBOARD_TAB_ACTIVE : DASHBOARD_TAB_INACTIVE,
            )}
            aria-pressed={active}
          >
            <t.Icon className="w-3 h-3" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const CATEGORIES = ["payment", "flight", "schedule", "card", "credit", "booking"];

// Server-recognised `?since=` shortcuts (see customer-activity-feed:
// resolveSinceFilter). `14d` is the server default but we send it
// explicitly so the URL is self-describing.
const TIME_WINDOWS = [
  { id: "7d",  label: "7 days" },
  { id: "14d", label: "14 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "all", label: "All time" },
];
const DEFAULT_TIME_WINDOW = "14d";

// Admin role check — mirrors the Edge function's `isAdmin()` and the
// existing pattern in Layout.jsx. Backed by JWT app_metadata.roles
// (which users CANNOT self-edit, unlike user_metadata).
function isCurrentUserAdmin(u) {
  if (!u) return false;
  const roles = u?.app_metadata?.roles || u?.appMetadata?.roles || [];
  return (Array.isArray(roles) && roles.includes("admin")) || u?.role === "admin";
}

export default function Activity() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tripId = params.get("trip_id");
  const viewUserId = params.get("user_id");
  const initialKinds = (params.get("kinds") || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => CATEGORIES.includes(s));

  const initialSince = (() => {
    const raw = (params.get("since") || "").trim().toLowerCase();
    return TIME_WINDOWS.some((w) => w.id === raw) ? raw : DEFAULT_TIME_WINDOW;
  })();

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [events, setEvents] = useState([]);
  const [tripReference, setTripReference] = useState(null);
  const [mode, setMode] = useState(null); // 'all_users' | 'single_user' | 'single_trip'
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeCategories, setActiveCategories] = useState(new Set(initialKinds));
  const [sinceWindow, setSinceWindow] = useState(initialSince);
  // Top-level view toggle. `activity` is the historical feed (unchanged
  // behavior). `upcoming` swaps in the NMI cron-monitoring board so the team
  // can spot bookings whose next charge is about to fail (past-due, retrying,
  // stale CIT). URL-mirrored as ?view= so the team can deep-link to it.
  const initialView = params.get("view") === "upcoming" ? "upcoming" : "activity";
  const [view, setView] = useState(initialView);

  const updateViewInUrl = useCallback((nextView) => {
    const next = new URLSearchParams(location.search);
    if (nextView === "upcoming") next.set("view", "upcoming");
    else next.delete("view");
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, "")) {
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Reflect filter selection back into the URL so the page is shareable.
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    const list = [...activeCategories];
    if (list.length === 0) next.delete("kinds");
    else next.set("kinds", list.join(","));
    if (sinceWindow === DEFAULT_TIME_WINDOW) next.delete("since");
    else next.set("since", sinceWindow);
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
    // location.pathname/search intentionally omitted so we don't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategories, sinceWindow]);

  // Resolve current user once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await UserEntity.me();
        if (!cancelled) setUser(me || null);
      } catch (e) {
        console.warn("Activity: UserEntity.me failed", e);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchPage = useCallback(async (opts = {}) => {
    const { cursor = null, replace = true } = opts;
    if (replace) {
      if (events.length === 0) setLoading(true);
      else setRefreshing(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    try {
      const { supabase } = await import("@/lib/supabase");
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError("Sign in to view your activity.");
        setEvents([]);
        setNextCursor(null);
        return;
      }

      const url = new URL(customerActivityFeedUrl());
      url.searchParams.set("page_size", "50");
      if (cursor) url.searchParams.set("cursor", cursor);
      if (tripId) url.searchParams.set("trip_id", tripId);
      if (viewUserId) url.searchParams.set("user_id", viewUserId);
      const activeList = [...activeCategories];
      if (activeList.length > 0) url.searchParams.set("kinds", activeList.join(","));
      // Always send the time window — the edge fn defaults to 14d when
      // `since` is missing, but being explicit keeps the request shape
      // legible in network logs and makes "all" a real opt-in.
      url.searchParams.set("since", sinceWindow);

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.error || `Request failed (${res.status})`);
        return;
      }
      const incoming = Array.isArray(body.events) ? body.events : [];
      setEvents((prev) => (replace ? incoming : [...prev, ...incoming]));
      setNextCursor(body.next_cursor ?? null);
      // _meta.mode tells us what scope the Edge function used. Drives the
      // "showCustomer" toggle on event cards + the header banner copy.
      setMode(body?._meta?.mode ?? null);
      // Capture trip reference when filtered to a single trip — used for the
      // header chip. The Edge function attaches it to every event under that
      // booking, so the first event is enough.
      if (tripId && incoming.length > 0) {
        const ref = incoming.find((e) => e.trip_reference)?.trip_reference;
        if (ref) setTripReference(ref);
      } else if (!tripId) {
        setTripReference(null);
      }
    } catch (e) {
      console.error("Activity: fetch failed", e);
      setError(e?.message || "Could not load activity");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
    // tripId / viewUserId / activeCategories / sinceWindow are stable
    // strings/sets we hash through useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, viewUserId, activeCategories, sinceWindow]);

  useEffect(() => {
    if (!authReady) return;
    if (!user) return;
    // Defense in depth: don't even hit the API for non-admins. The Edge
    // function would 403 anyway, but skipping the request keeps the
    // network panel quiet + avoids surfacing a "Forbidden" toast for
    // someone who guessed the URL.
    if (!isCurrentUserAdmin(user)) return;
    fetchPage({ cursor: null, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, user, tripId, viewUserId, [...activeCategories].join(","), sinceWindow]);

  const toggleCategory = (cat) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const groups = useMemo(() => groupEventsByDay(events), [events]);
  const filterActive = activeCategories.size > 0 || !!tripId || !!viewUserId;
  const admin = isCurrentUserAdmin(user);
  const showCustomerOnCards = mode === "all_users";

  if (authReady && !user) {
    return (
      <div className="pb-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-6">
          <h2 className="text-3xl md:text-[32px] font-light text-white tracking-tight leading-none mb-3">Activity</h2>
          <div className={cn(CART_SECTION_CARD, "rounded-2xl")}>
            <div className="p-5 text-center">
              <p className="text-sm text-white/70 font-normal mb-3">Sign in to view your activity.</p>
              <Button asChild size="sm" className="bg-white text-black hover:bg-white/90 font-normal h-8 text-xs">
                <Link to="/home?auth_mode=login">Sign in</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Strict admin-only gate. Migration 079 dropped the per-user RLS read
  // policy on customer_action_log and the Edge function returns 403 for
  // non-admin JWTs — this UI block is the third layer, surfacing a clean
  // "no access" state instead of a confused empty page if a customer
  // somehow lands on /activity (bookmarked URL, etc.).
  if (authReady && user && !admin) {
    return (
      <div className="pb-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-6">
          <h2 className="text-3xl md:text-[32px] font-light text-white tracking-tight leading-none mb-3">Activity</h2>
          <div className={cn(CART_SECTION_CARD, "rounded-2xl border-red-400/30")}>
            <div className="p-4 flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-white font-normal mb-0.5">This page is restricted</p>
                <p className="text-white/60 text-xs font-normal">
                  The Activity feed is an internal admin tool. If you think you should have access, contact support.
                </p>
                <div className="mt-3">
                  <Button asChild size="sm" variant="outline" className={cn("h-7 text-xs font-normal text-white border", DASHBOARD_TAB_INACTIVE)}>
                    <Link to="/dashboard">Back to Trips</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Header banner copy reflects the current scope. "All customers" when
  // no filters are set; otherwise either trip-scope or user-scope.
  const scopeBanner = (() => {
    if (tripId) {
      return `Viewing trip ${tripReference || tripId.slice(0, 8)}`;
    }
    if (viewUserId) {
      // Prefer the user's display name; fall back to email, then to a
      // short uuid fragment so the banner never reads "Viewing null".
      const sampleEvent = events.find((e) => e.user_id === viewUserId);
      const label = sampleEvent?.user_name || sampleEvent?.user_email;
      return `Viewing ${label || `user ${viewUserId.slice(0, 8)}…`}`;
    }
    return "Viewing activity across all customers";
  })();

  const canClearScope = !!tripId || !!viewUserId;
  const clearScope = () => {
    const next = new URLSearchParams(location.search);
    next.delete("trip_id");
    next.delete("user_id");
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });
  };

  return (
    <div className="pb-12 relative">
      <RoyalAmbientBackground />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 md:py-3 relative z-10">
        {/* Masthead — matches the Dashboard "Trips" header: light 32px title,
            tight subtitle underneath, view toggle anchored to the right of
            the same row on desktop and below on mobile. No card chrome so
            the page reads as a real product surface, not a marketing hero. */}
        <motion.div
          className="mb-2.5 mt-1"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-6">
            <div className="min-w-0">
              <h1 className="text-3xl md:text-[32px] font-light text-white tracking-tight leading-none">
                Activity
              </h1>
              <p className="text-white/60 text-[12.5px] font-normal mt-2 leading-none">
                {view === "upcoming"
                  ? "Bookings with an open NMI installment — verifies the daily cron."
                  : "Booking events across customers — payments, flights, schedule, cards, credits."}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ViewModeToggle
                value={view}
                onChange={(v) => { setView(v); updateViewInUrl(v); }}
              />
              {view === "activity" ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white/55 hover:text-white hover:bg-brand-900/40 rounded-full h-8 w-8 border border-brand-800/40"
                  onClick={() => fetchPage({ cursor: null, replace: true })}
                  disabled={refreshing || loading}
                  aria-label="Refresh"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </Button>
              ) : null}
            </div>
          </div>
        </motion.div>

        {view === "upcoming" ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <UpcomingNmiList />
          </motion.div>
        ) : (
          <>
        {/* Scope banner — hairline pill that calls out the lens (single trip,
            single user, or all customers) without competing with the title. */}
        <motion.div
          className="mb-2 flex items-center gap-2 flex-wrap"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
        >
          <span className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-brand-900/30 border border-brand-800/40 text-white/85 text-[10px] font-normal">
            <Filter className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
            {scopeBanner}
            {canClearScope ? (
              <button
                onClick={clearScope}
                className="text-white/45 hover:text-white"
                aria-label="Clear scope filter"
              >
                <X className="w-3 h-3" />
              </button>
            ) : null}
          </span>
        </motion.div>

        {/* Category chips — sticky on mobile, brand-gradient pills on the active
            chip to match the Dashboard status tabs. */}
        <motion.div
          className="sticky top-[64px] md:static z-10 -mx-4 md:mx-0 px-4 md:px-0 mb-3 py-1.5 md:py-0 bg-black/80 md:bg-transparent backdrop-blur md:backdrop-blur-0"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mb-0.5 scrollbar-none">
            <FilterChip
              active={activeCategories.size === 0}
              onClick={() => setActiveCategories(new Set())}
            >
              All
            </FilterChip>
            {CATEGORIES.map((cat) => (
              <FilterChip
                key={cat}
                active={activeCategories.has(cat)}
                onClick={() => toggleCategory(cat)}
              >
                {CATEGORY_META[cat].label}
              </FilterChip>
            ))}
          </div>
        </motion.div>

        {/* Time-window selector — defaults to the last 14 days so the admin
            all-users view shows recent, actionable activity instead of years
            of "Trip booked" / legacy-cancellation noise. "All time" is one
            click away. URL-mirrored as ?since= so the page is shareable. */}
        <motion.div
          className="mb-3 flex items-center gap-1.5 flex-wrap"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
        >
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-normal pl-1 pr-1">
            Window
          </span>
          {TIME_WINDOWS.map((w) => (
            <FilterChip
              key={w.id}
              active={sinceWindow === w.id}
              onClick={() => setSinceWindow(w.id)}
            >
              {w.label}
            </FilterChip>
          ))}
        </motion.div>

        {/* Body */}
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <ErrorState message={error} onRetry={() => fetchPage({ cursor: null, replace: true })} />
        ) : events.length === 0 ? (
          <EmptyState filterActive={filterActive} />
        ) : (
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            {groups.map((g) => (
              <section key={g.label}>
                <h3 className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-normal mb-1 px-1">
                  {g.label}
                </h3>
                <div className="space-y-1">
                  {g.events.map((e) => (
                    <EventCard key={e.id} event={e} showCustomer={showCustomerOnCards} />
                  ))}
                </div>
              </section>
            ))}

            {nextCursor ? (
              <div className="pt-1">
                <Button
                  variant="outline"
                  className={cn("w-full h-8 rounded-full text-xs font-normal text-white border", DASHBOARD_TAB_INACTIVE)}
                  disabled={loadingMore}
                  onClick={() => fetchPage({ cursor: nextCursor, replace: false })}
                >
                  {loadingMore ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>
                      Load more
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </>
                  )}
                </Button>
              </div>
            ) : events.length > 0 ? (
              <div className="pt-1 text-center text-white/35 text-[10px] font-normal flex items-center justify-center gap-1.5">
                <Clock className="w-3 h-3" />
                You're all caught up
              </div>
            ) : null}
          </motion.div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
